import React from 'react'
import { DownloadOutlined, SyncOutlined } from '@ant-design/icons'

import { CheckboxOffSvg, CheckboxOnSvg, FilterSvg, SearchSvg } from './icons'
import styles from './BenefitsToolbar.module.scss'

interface BenefitsToolbarProps {
  /** Live (uncommitted) search box value. */
  search: string
  onSearchChange: (value: string) => void
  /** Commit a search (also resets to page 1 in the parent). */
  onApplySearch: (value: string) => void
  /** Currently applied search — lets a cleared box auto-apply empty. */
  appliedSearch: string
  activeFilterCount: number
  filtersOpen: boolean
  onOpenFilters: () => void
  isFetching: boolean
  onRefresh: () => void
  showDetails: boolean
  onToggleDetails: () => void
  /** Export the currently-loaded page to a styled XLSX. */
  onExport: () => void
  /** True while the workbook is building — disables the button and spins it. */
  exporting: boolean
}

const BenefitsToolbar: React.FC<BenefitsToolbarProps> = ({
  search,
  onSearchChange,
  onApplySearch,
  appliedSearch,
  activeFilterCount,
  filtersOpen,
  onOpenFilters,
  isFetching,
  onRefresh,
  showDetails,
  onToggleDetails,
  onExport,
  exporting,
}) => (
  <div className={styles.topBar}>
    <div className={styles.searchBox}>
      <input
        placeholder="Search by employee or benefit ID"
        value={search}
        onChange={(e) => {
          const v = e.target.value
          onSearchChange(v)
          if (v === '' && appliedSearch) onApplySearch('')
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onApplySearch(search)
        }}
      />
      <button
        type="button"
        aria-label="Search"
        className={styles.searchIconSeg}
        onClick={() => onApplySearch(search)}
      >
        <SearchSvg size={15} />
      </button>
    </div>

    <button
      type="button"
      title="Filters"
      className={`${styles.iconBtn}${
        filtersOpen || activeFilterCount > 0 ? ` ${styles.active}` : ''
      }`}
      onClick={onOpenFilters}
    >
      <FilterSvg size={16} />
      {activeFilterCount > 0 && (
        <span className={styles.badge}>{activeFilterCount}</span>
      )}
    </button>

    {/* Not in the design mock; kept so operators can force a refetch. */}
    <button
      type="button"
      title="Refresh"
      className={styles.iconBtn}
      onClick={onRefresh}
      disabled={isFetching}
    >
      <SyncOutlined spin={isFetching} style={{ fontSize: 15 }} />
    </button>

    {isFetching && <span className={styles.updatingText}>Updating…</span>}

    <div className={styles.spacer} />

    <button
      type="button"
      title="Export the filtered benefits to Excel"
      className={styles.exportBtn}
      onClick={onExport}
      disabled={exporting}
    >
      {exporting ? (
        <SyncOutlined spin style={{ fontSize: 14 }} />
      ) : (
        <DownloadOutlined style={{ fontSize: 14 }} />
      )}
      {exporting ? 'Exporting…' : 'Export'}
    </button>

    <button
      type="button"
      className={`${styles.detailsToggle}${
        showDetails ? ` ${styles.active}` : ''
      }`}
      onClick={onToggleDetails}
    >
      {showDetails ? <CheckboxOnSvg /> : <CheckboxOffSvg />}
      Show details
    </button>
  </div>
)

export default BenefitsToolbar
